import * as THREE from 'three';
import { maxAmplitude, type Wave } from '@sea/world';

const WAVE_COUNT = 6;

/** 近场：细网格，负责「肉眼能看到的浪」 */
const NEAR_SIZE = 320;
const NEAR_SEGMENTS = 160; // 2 m/顶点，最短波长 4.5 m 仍有 2 个采样
/** 远场：粗网格，只负责把海铺到地平线，细节由雾吃掉 */
const FAR_SIZE = 3200;
const FAR_SEGMENTS = 64;

const vertexShader = /* glsl */ `
  uniform float uTime;
  uniform vec4 uWaveA[${WAVE_COUNT}];   // (dirX, dirZ, amplitude, k)
  uniform vec4 uWaveB[${WAVE_COUNT}];   // (omega, phase, ..)

  varying vec3 vWorld;
  varying vec3 vNormal;
  varying float vHeight;

  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);

    float h = 0.0;
    vec2 slope = vec2(0.0);
    for (int i = 0; i < ${WAVE_COUNT}; i++) {
      vec4 a = uWaveA[i];
      vec4 b = uWaveB[i];
      float phase = a.w * dot(a.xy, wp.xz) - b.x * uTime + b.y;
      float s = sin(phase);
      float c = cos(phase);
      h += a.z * s;
      slope += (a.z * c * a.w) * a.xy;
    }

    wp.y += h;
    vWorld = wp.xyz;
    vHeight = h;
    vNormal = normalize(vec3(-slope.x, 1.0, -slope.y));
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

const fragmentShader = /* glsl */ `
  precision highp float;

  uniform vec3 uSunDir;
  uniform vec3 uSunColor;
  uniform vec3 uDeep;
  uniform vec3 uShallow;
  uniform vec3 uFogColor;
  uniform vec3 uCamPos;
  uniform float uFogNear;
  uniform float uFogFar;
  uniform float uMaxAmp;
  // 顶点里声明的 uniform 不会跨阶段共享，片元用到就必须自己再声明一次
  uniform float uTime;
  /** 船体在水平面上的投影：(cx, cz, rx, rz)，把这块水挖掉 */
  uniform vec4 uHole;

  varying vec3 vWorld;
  varying vec3 vNormal;
  varying float vHeight;

  void main() {
    // 船体占位：把 hull 投影范围内的水面直接丢掉。
    // 不做这一步，波峰会从甲板「长」出来把船淹掉——低头只能看到水。
    vec2 hp = (vWorld.xz - uHole.xy) / uHole.zw;
    if (dot(hp, hp) < 1.0) discard;

    vec3 N = normalize(vNormal);
    vec3 V = normalize(uCamPos - vWorld);
    float ndv = max(dot(N, V), 0.0);

    // 高频细节：不占几何面数，只扰动法线。没有它，海面在近处看起来像一块塑料板。
    vec2 p = vWorld.xz;
    vec2 d1 = vec2(0.91, 0.41);
    vec2 d2 = vec2(-0.37, 0.93);
    vec2 d3 = vec2(0.62, -0.78);
    float s1 = cos(dot(p, d1) * 1.7 - uTime * 2.3);
    float s2 = cos(dot(p, d2) * 3.3 - uTime * 3.7);
    float s3 = cos(dot(p, d3) * 6.1 - uTime * 5.3);
    vec2 detail = d1 * s1 * 0.075 + d2 * s2 * 0.045 + d3 * s3 * 0.028;
    N = normalize(N + vec3(detail.x, 0.0, detail.y));

    // 菲涅尔：掠射角反射天空色。注意 eyeline 只有 1.6 m，绝大多数水面都在掠射角上，
    // 如果用满强度（F→1）整个海面会被天空色洗白——所以只取 38%，剩下的靠浪高与高光读结构。
    float F = pow(max(1.0 - ndv, 0.0), 5.0);
    vec3 base = mix(uDeep, uShallow, clamp(0.35 + vHeight * 2.2, 0.0, 1.0));
    vec3 skyTint = mix(uShallow * 1.15, uFogColor, 0.85);
    vec3 col = mix(base, skyTint, F * 0.38);

    // 太阳碎光
    vec3 H = normalize(normalize(uSunDir) + V);
    float spec = pow(max(dot(N, H), 0.0), 180.0);
    col += uSunColor * spec * 3.0;

    // 浪尖白沫
    float crest = smoothstep(0.55 * uMaxAmp, 0.95 * uMaxAmp, vHeight);
    float streak = smoothstep(0.35, 0.9, s2 * 0.5 + 0.5);
    col = mix(col, vec3(0.84, 0.9, 0.95), crest * streak * 0.7);

    // 距离雾：远处收向「比天空略深一点」的海色，而不是直接变成雾色，
    // 否则海与天会糊成一片没有地平线。
    vec3 farSea = mix(uFogColor, uShallow * 0.8, 0.32);
    float d = distance(uCamPos, vWorld);
    col = mix(col, farSea, smoothstep(uFogNear, uFogFar, d));

    gl_FragColor = vec4(col, 1.0);
    #include <colorspace_fragment>
  }
`;

export interface OceanOptions {
  fogNear: number;
  fogFar: number;
  fogColor: THREE.Color;
  sunDirection: THREE.Vector3;
  sunColor: THREE.Color;
}

/**
 * 海面（两级网格）。
 *
 * 铁律（改之前读 §3.2）：
 *  1. 顶点位移全在 shader 里，CPU 一帧都不碰几何；
 *  2. 波公式必须与 @sea/world 的 waveHeight 完全一致，否则船会浮在浪外面；
 *  3. 网格跟着相机平移，但相位用**世界坐标**算，否则浪会跟着你走；
 *  4. 近场细、远场粗——单张网格要么糊（段数少）要么崩（段数多）。
 */
export class Ocean {
  readonly group = new THREE.Group();

  private readonly mat: THREE.ShaderMaterial;
  private readonly waveA: THREE.Vector4[] = [];
  private readonly waveB: THREE.Vector4[] = [];
  private readonly near: THREE.Mesh;
  private readonly far: THREE.Mesh;

  constructor(waves: readonly Wave[], options: OceanOptions) {
    for (let i = 0; i < WAVE_COUNT; i++) {
      this.waveA.push(new THREE.Vector4(0, 0, 0, 0));
      this.waveB.push(new THREE.Vector4(0, 0, 0, 0));
    }

    this.mat = new THREE.ShaderMaterial({
      fog: false,
      side: THREE.DoubleSide,
      uniforms: {
        uTime: { value: 0 },
        uWaveA: { value: this.waveA },
        uWaveB: { value: this.waveB },
        uMaxAmp: { value: 1 },
        uSunDir: { value: options.sunDirection.clone() },
        uSunColor: { value: options.sunColor.clone() },
        uDeep: { value: new THREE.Color('#0d2b3a') },
        uShallow: { value: new THREE.Color('#2c7d8c') },
        uFogColor: { value: options.fogColor.clone() },
        uCamPos: { value: new THREE.Vector3() },
        uFogNear: { value: options.fogNear },
        uFogFar: { value: options.fogFar },
        // 略小于船体轮廓：保证船舷永远盖住洞的边缘，不会看穿
        uHole: { value: new THREE.Vector4(0, 0, 2.02, 0.6) },
      },
      vertexShader,
      fragmentShader,
    });

    const nearGeo = new THREE.PlaneGeometry(NEAR_SIZE, NEAR_SIZE, NEAR_SEGMENTS, NEAR_SEGMENTS);
    nearGeo.rotateX(-Math.PI / 2);
    this.near = new THREE.Mesh(nearGeo, this.mat);
    this.near.frustumCulled = false;
    this.near.renderOrder = 1;

    const farGeo = new THREE.PlaneGeometry(FAR_SIZE, FAR_SIZE, FAR_SEGMENTS, FAR_SEGMENTS);
    farGeo.rotateX(-Math.PI / 2);
    this.far = new THREE.Mesh(farGeo, this.mat);
    this.far.frustumCulled = false;
    this.far.renderOrder = 0;
    // 远场压低 25 cm：与近场重叠处避免 z-fighting，在雾里完全看不出来
    this.far.position.y = -0.25;

    this.group.add(this.far, this.near);
    this.setWaves(waves);
  }

  /** 换海况：重新填 6 个波（不足补零），不重编译 shader。 */
  setWaves(waves: readonly Wave[]): void {
    for (let i = 0; i < WAVE_COUNT; i++) {
      const w = waves[i];
      const a = this.waveA[i]!;
      const b = this.waveB[i]!;
      if (w) {
        a.set(w.dirX, w.dirZ, w.amplitude, w.k);
        b.set(w.omega, w.phase, 0, 0);
      } else {
        a.set(0, 0, 0, 0);
        b.set(0, 0, 0, 0);
      }
    }
    this.mat.uniforms.uMaxAmp!.value = Math.max(0.001, maxAmplitude(waves));
  }

  update(time: number, cameraPos: THREE.Vector3): void {
    this.group.position.set(cameraPos.x, 0, cameraPos.z);
    this.mat.uniforms.uTime!.value = time;
    (this.mat.uniforms.uCamPos!.value as THREE.Vector3).copy(cameraPos);
  }

  /** 调整船体挖洞范围（米）：rx 沿 X，rz 沿 Z */
  setHole(centerX: number, centerZ: number, radiusX: number, radiusZ: number): void {
    (this.mat.uniforms.uHole!.value as THREE.Vector4).set(centerX, centerZ, radiusX, radiusZ);
  }

  /** 三角面计数（给性能自检用） */
  get triangleEstimate(): number {
    return NEAR_SEGMENTS * NEAR_SEGMENTS * 2 + FAR_SEGMENTS * FAR_SEGMENTS * 2;
  }

  dispose(): void {
    this.near.geometry.dispose();
    this.far.geometry.dispose();
    this.mat.dispose();
  }
}
